import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { journalService } from "~/services/contabilidad/journalService";
import type { JournalFilters, JournalFormData } from "~/types/contabilidad";

export const JOURNALS_QUERY_KEY = ["journals"];

export function useJournals(filters?: JournalFilters) {
  return useQuery({
    queryKey: [...JOURNALS_QUERY_KEY, filters],
    queryFn: () => journalService.getAll(filters),
  });
}

export function useJournalsByCompany(companyId: string) {
  return useQuery({
    queryKey: [...JOURNALS_QUERY_KEY, "company", companyId],
    queryFn: () => journalService.getByCompany(companyId),
    enabled: !!companyId,
  });
}

export function useJournal(id: string) {
  return useQuery({
    queryKey: [...JOURNALS_QUERY_KEY, id],
    queryFn: () => journalService.getById(id),
    enabled: !!id,
  });
}

export function useCreateJournal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: JournalFormData) => journalService.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: JOURNALS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...JOURNALS_QUERY_KEY, "company", data.company_id],
      });
      toast.success("Diario creado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al crear diario: ${error.message}`);
    },
  });
}

export function useUpdateJournal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<JournalFormData>;
    }) => journalService.update(id, data),
    onSuccess: (updatedJournal, { id }) => {
      queryClient.invalidateQueries({ queryKey: JOURNALS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...JOURNALS_QUERY_KEY, id] });
      queryClient.invalidateQueries({
        queryKey: [...JOURNALS_QUERY_KEY, "company", updatedJournal.company_id],
      });
      toast.success("Diario actualizado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar diario: ${error.message}`);
    },
  });
}

export function useDeleteJournal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => journalService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JOURNALS_QUERY_KEY });
      toast.success("Diario eliminado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar diario: ${error.message}`);
    },
  });
}
